import { cva } from 'class-variance-authority';

/**
 * Select class recipes.
 *
 * Zero `dark:` variants, as everywhere else in this layer. The closed control reads the
 * `--ds-input-*` slots and the menu the `--ds-select-*` ones, both set per theme in
 * `components/styles/design-system.css`.
 *
 * ── The closed control IS the text input's box ──────────────────────────────────────────────────
 *
 * Not a lookalike built to the same numbers — literally the same recipes. `Select` renders
 * `textInputBoxRecipe`, `textInputLabelRecipe`, `textInputHelperRecipe` and
 * `textInputAffixRecipe`, and this file adds only the parts a select has and an input does not:
 * the trigger, the caret and the menu.
 *
 * That is what makes the ticket's "must not drift from the text input" invariant true by
 * construction rather than by matching values that could later diverge. It works because Base UI's
 * `SelectRoot` feeds the surrounding `Field.Root` — `setFilled`, `setFocused`, `setTouched`,
 * `setDirty` — so `Field.Root` carries exactly the `data-focused` / `data-filled` / `data-invalid` /
 * `data-disabled` attributes the `group/ti` selectors in `TextInput.recipe` already key off. The
 * label shrinks, the border thickens and the ring appears with no new CSS at all.
 *
 * Verified against the design rather than assumed: the select's label line, value line, radius,
 * padding and 60px height are the same numbers as the text input's cells (compare nodes 498:41219
 * and 447:35138 — they differ only in the caret and, in light mode, one border step). See the note
 * on `selectCaretRecipe` for the one place the two shipped components disagree.
 */

/**
 * The trigger — a `<button>` that fills the whole box and pays all the padding itself.
 *
 * Same structural decision as the text input's control, for the same reason: pad the box instead and
 * the button becomes a thin strip across the middle of a 60px field, so most of what looks like the
 * control is dead to the pointer. Here it matters more than it does for an input, because the entire
 * field is meant to open the menu.
 *
 * `pt-[28px]` over a 20px line with `pb-[10px]` is 58px, plus the 1px border top and bottom — the
 * design's 60px, and the same arithmetic the text input uses, so the value text of a select and of
 * an input stacked beneath it sit on the same line.
 *
 * `text-left` is load-bearing: a `<button>` centres its text by default, and the value has to start
 * at the same x as the label above it.
 *
 * `pr-[50px]` is unconditional because the caret is not optional — 24px gutter, 16px of glyph, 10px
 * of gap. The value truncates against it rather than running underneath.
 */
export const selectTriggerRecipe = cva(
  [
    'relative m-0 block w-full appearance-none border-0 bg-transparent text-left',
    'font-sans text-[14px] leading-5 text-[var(--ds-input-value)]',
    'outline-none',
    'pr-[50px]',
    'cursor-pointer',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-input-value-disabled)]',
  ],
  {
    variants: {
      /**
       * Declared with no classes of their own: the vertical inset depends on BOTH of these at once,
       * so it is emitted from `compoundVariants` below and nowhere else.
       *
       * Split that way deliberately rather than for tidiness. A `py-[19px]` here alongside a
       * `pb-[10px]` there would put two competing utilities on one element, and which won would be
       * decided by their order in the generated stylesheet rather than by the order they are listed
       * in — `cva` composes with `clsx`, which concatenates and does not resolve Tailwind conflicts.
       */
      labelHidden: { true: '', false: '' },
      hasHelper: { true: '', false: '' },
      /** 24px gutter, plus 16px of icon and a 10px gap when the field's own leading slot is filled. */
      hasLeftIcon: { true: 'pl-[50px]', false: 'pl-[24px]' },
    },
    compoundVariants: [
      /**
       * The drawn field. `pt-[28px]` clears the SHRUNK label — 10px to its line plus the line itself
       * — which is what leaves the value on the design's line, and `pb-[10px]` completes the 58px
       * interior of a 60px box.
       */
      { labelHidden: false, hasHelper: false, class: 'pt-[28px] pb-[10px]' },
      // With helper text the bottom inset moves to the box — see `textInputBoxRecipe`.
      { labelHidden: false, hasHelper: true, class: 'pt-[28px] pb-0' },
      /**
       * Hidden label: nothing sits above the value, so the inset that cleared it would strand the
       * text in the bottom third of a box that now looks empty above it. `py-[19px]` centres the
       * same 20px line in the same 60px field (19 + 20 + 19, plus the box's 1px border top and
       * bottom), so only the value moves and the height is untouched.
       */
      { labelHidden: true, hasHelper: false, class: 'py-[19px]' },
      { labelHidden: true, hasHelper: true, class: 'pt-[19px] pb-0' },
    ],
    defaultVariants: { labelHidden: false, hasHelper: false, hasLeftIcon: false },
  }
);

/**
 * The value text inside the trigger.
 *
 * `truncate` needs a block with a width to work against, which the trigger's own padding gives it.
 * An empty select renders no value at all — the unshrunk label occupies the value line instead,
 * which is exactly what `Content=Empty` draws (node 498:41837). So there is no placeholder colour
 * here: there is no placeholder.
 *
 * `min-h-[20px]` is what keeps the value line present when there is no value, and it is load-bearing
 * rather than defensive. `Select.Value` renders NOTHING while empty, so without a floor this span
 * collapses to zero and the trigger measures 38px instead of 58 — the box still looks right, because
 * it has its own `min-h-[60px]`, but the bottom third of an empty field stops being a click target
 * and stops opening the menu. Caught by measuring the anchor, not by looking at it.
 */
export const selectValueRecipe = cva(['block min-h-[20px] truncate']);

/**
 * The caret, and ONLY its rotation — position comes from `textInputAffixRecipe({ side: 'right' })`,
 * which the component composes with this.
 *
 * Sharing the position is a deliberate choice over reproducing the cell, and it is the one place the
 * shipped family knowingly differs from the file. Both sets draw their trailing glyph as a child of
 * the 34px content row, which centres it 20px from the top of the box; the shipped text input places
 * it 22px down, centred in the 60px field instead. Two pixels, on a component already in `main`.
 *
 * Matching the text input keeps the caret and an input's trailing icon on the same line when the two
 * are stacked, which is the invariant the ticket actually states. Matching the file instead would fix
 * the select and leave the family inconsistent. Raised for design as one question covering both
 * components rather than half-fixed here.
 *
 * The 180° flip on open is authored, not decoration: the Active cells rotate the glyph (the export
 * shows `-rotate-180 -scale-x-100` on node I505:11791). `Select.Icon` carries `data-popup-open`, so
 * the menu's open state drives it with no React state involved.
 */
export const selectCaretRecipe = cva([
  'transition-transform duration-150 motion-reduce:transition-none',
  'data-[popup-open]:rotate-180',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500), because a
 * select inside a legacy MUI `Dialog` is the ordinary case during the refresh and a popup trapped
 * behind the dialog it belongs to is unusable. The value is MUI's scale rather than this layer's
 * because MUI is what it has to coexist with.
 *
 * The literal is REPEATED in `menuPositionerRecipe` (1400) and `dialogRecipe` (1300) rather than
 * shared from one constant, and it cannot be otherwise: Tailwind generates a utility only for a class
 * name it finds written out in the source, so `z-[${SOME_CONSTANT}]` would compile to a class with no
 * rule behind it. All three sites are named here so a change to one can find the others.
 */
export const selectPositionerRecipe = cva(['z-[1400]']);

/**
 * The menu.
 *
 * `ds-select` is repeated here by the component rather than inherited: the popup is portalled into
 * `<body>`, so it has none of the trigger's ancestors and would otherwise resolve every slot to its
 * light-mode fallback. The theme class is NOT repeated here — `Select` puts it on `Select.Portal`,
 * which is an ancestor of this element, so the ordinary `:where(.dark) .ds-select` rule reaches it.
 *
 * The design draws the panel 2px wider than the trigger and offset 1px left (node 556:20985 —
 * `left: -1px`, `right: -1px`), so the two borders' outer edges line up rather than the panel sitting
 * inside the field. `--anchor-width` is Base UI's measurement of the trigger.
 *
 * `max-h-[--available-height]` plus scrolling is what keeps a long list on screen near the bottom of
 * the viewport; Base UI computes the value from the collision boundary.
 */
export const selectMenuRecipe = cva([
  'box-border flex flex-col',
  'w-[calc(var(--anchor-width)_+_2px)]',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'px-[12px] py-[16px]',
  'rounded-[20px]',
  'border-solid border-[length:1px] border-[var(--ds-select-menu-border)]',
  'bg-[var(--ds-select-menu-bg)]',
  'outline-none',
]);

/**
 * An option row.
 *
 * 13px Medium on a 20px line with 6px of inset is the design's 32px row (Body XS Med, node
 * 556:20715) — a step down from the trigger's 14px Regular, which is authored rather than an
 * oversight.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI's
 * `highlightItemOnHover` defaults to true. That is what the design wants: it draws one
 * `hover-inactive` treatment and no separate keyboard-focus row, so pointer and keyboard users see
 * the same thing and there is no second state to keep in step.
 *
 * The FILL is shared between selected and highlighted — one value, `--ds-select-item-bg-hover`,
 * drawn for either — so the current option reads as the row the pointer would land on. Because both
 * selectors resolve to the same colour there is no precedence question on the background at all.
 *
 * The TEXT COLOUR is deliberately not shared. Selected keeps its own, a step stronger than hovered
 * (Dark/1 against Dark/5 in light), which is what keeps the selected row distinguishable from
 * whichever row happens to be under the pointer now that the two share a fill.
 *
 * The text selectors stay mutually exclusive rather than order-dependent, as in the sibling recipes.
 * Precedence: disabled → selected → highlighted → resting. Selected outranks highlighted so that
 * arrow-keying past the current option does not make it look unselected.
 *
 * The selected row's mark is its colour AND the trailing indicator — never colour alone, which is
 * the point of `selectItemIndicatorRecipe`.
 */
export const selectItemRecipe = cva([
  'group/si',
  'relative flex w-full shrink-0 items-center gap-[8px]',
  'm-0 appearance-none border-0 bg-transparent text-left',
  'px-[12px] py-[6px]',
  'rounded-[24px]',
  'font-sans text-[13px] leading-5 font-medium select-none',
  'outline-none',
  'cursor-pointer',
  'transition-[color,background-color] duration-150 motion-reduce:transition-none',

  // Resting, out-specified by everything below.
  'text-[var(--ds-select-item-fg)]',

  // One fill for both states — see the note above on why the background is shared and the text is not.
  'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-select-item-bg-hover)]',
  'data-[selected]:not-data-[disabled]:bg-[var(--ds-select-item-bg-hover)]',

  'data-[highlighted]:not-data-[selected]:not-data-[disabled]:text-[var(--ds-select-item-fg-hover)]',
  'data-[selected]:not-data-[disabled]:text-[var(--ds-select-item-fg-selected)]',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
]);

/**
 * A per-option leading glyph. Sized and boxed like the field's own affixes so a 16px icon lands the
 * same way in both places.
 */
export const selectItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  '[&_svg]:size-full',
]);

/** The option label. Flexes so the trailing indicator stays pinned right as the row grows. */
export const selectItemLabelRecipe = cva(['min-w-0 flex-1 truncate']);

/**
 * A per-option trailing cell — the caller's own content, positioned and nothing else.
 *
 * `shrink-0` against the label's `flex-1 truncate` is the whole point: a long label truncates and
 * the cell keeps its width, rather than the two fighting over the row and the badge collapsing to
 * an ellipsis.
 *
 * No colour, no padding, no background. This layer has no status vocabulary of its own and the
 * design draws no cell here (see `types.ts`), so anything drawn would be invented chrome. A caller
 * wanting a grey pill passes `Tag`.
 */
export const selectItemTrailingRecipe = cva(['inline-flex shrink-0 items-center']);

/**
 * The trailing indicator on the selected row, mounted by Base UI only while that row is selected.
 *
 * This is the accessibility half of the selected treatment, not decoration. The row's other
 * distinction is its text colour, and colour alone cannot carry state — the indicator is what a
 * low-vision or colour-blind user sees, alongside the `aria-selected` Base UI already sets.
 *
 * It takes its OWN colour rather than the selected row's text colour, because the design draws it
 * green (nodes 556:20716 light, 556:20471 dark). That makes the selected row differ from the others
 * in shape and in hue, not just in contrast — a better signal than a tick in the text colour, so it
 * is reproduced as authored rather than folded into `-item-fg-selected`.
 */
export const selectItemIndicatorRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[var(--ds-select-item-indicator)]',
  '[&_svg]:size-full',
]);
